import React, { useState, useEffect } from 'react';
import { X, Users, Car, Clock, ChevronDown, Plus, MapPin } from 'lucide-react';
import { Customer, CustomerVehicle, getCustomer } from '../lib/customers';
import { useBusiness } from '../contexts/BusinessContext';
import CreateScheduleEditor from './booking/CreateScheduleEditor';
import { createBooking } from '../lib/bookings';
import SelectResourceModal from './manage/SelectResourceModal';
import EditVehicleModal from './booking/EditVehicleModal';
import EditCustomerModal from './manage/EditCustomerModal';
import { Service, ServiceAddon, getServiceAddons } from '../lib/services';
import SelectCustomerModal from './manage/SelectCustomerModal';
import EditServiceModal from './booking/EditServiceModal';
import CreateVehicleModal from './CreateVehicleModal';
import SelectVehicleModal from './manage/SelectVehicleModal';
import SelectServiceModal from './manage/SelectServiceModal';
import RichTextDescription from './RichTextDescription';
import { supabase } from '../lib/supabase';
import SelectServiceAddonsModal from './manage/SelectServiceAddonsModal';
import { executeScheduledTriggers } from '../services/scheduledTriggerProcessor';
import { triggerBookingAutomations } from '../lib/automationsV2';
import { Booking } from '../lib/bookings';
import AddressAutocomplete from './AddressAutocomplete';
import { CustomerVehicle as CustomerVehicleType } from '../types/customers';
import { useEventDrivenModalPersistence } from '../utils/modalPersistence';
import ColorPickerModal from './ColorPickerModal';
import CreateColorPresetModal from './CreateColorPresetModal';
import { getColorPresets, createColorPreset, deleteColorPreset, ColorPreset } from '../lib/color-presets';

// Extend Service interface to include tax_rate
interface ExtendedService extends Service {
  tax_rate?: number;
  tax_exempt?: boolean;
}

interface CreateBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  initialDate?: Date | null;
}

interface SavedColor {
  id: string;
  name: string;
  color: string;
  secondary_color?: string;
}

export default function CreateBookingModal({
  isOpen,
  onClose,
  onSuccess,
  initialDate
}: CreateBookingModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [title, setTitle] = useState('Booking');
  const [isLocationBooking, setIsLocationBooking] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedServices, setSelectedServices] = useState<Array<{
    vehicle: CustomerVehicle;
    service: ExtendedService;
    priceOption: any;
  }>>([]);
  const [currentVehicle, setCurrentVehicle] = useState<CustomerVehicle | null>(null);
  const [isSelectCustomerModalOpen, setIsSelectCustomerModalOpen] = useState(false);
  const [isSelectVehicleModalOpen, setIsSelectVehicleModalOpen] = useState(false);
  const [isCreateVehicleModalOpen, setIsCreateVehicleModalOpen] = useState(false);
  const [isEditCustomerModalOpen, setIsEditCustomerModalOpen] = useState(false);
  const [isEditServiceModalOpen, setIsEditServiceModalOpen] = useState(false);
  const [selectedServiceToEdit, setSelectedServiceToEdit] = useState<any>(null);
  const [isEditVehicleModalOpen, setIsEditVehicleModalOpen] = useState(false);
  const [selectedVehicleToEdit, setSelectedVehicleToEdit] = useState<CustomerVehicle | null>(null);
  const [isSelectServiceModalOpen, setIsSelectServiceModalOpen] = useState(false);
  const [isSelectResourceModalOpen, setIsSelectResourceModalOpen] = useState(false);
  const [selectedResources, setSelectedResources] = useState<any[]>([]);
  const [scheduleData, setScheduleData] = useState({
    dropOffDate: '',
    dropOffTime: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    customer_notes: ''
  });
  const [bookingColor, setBookingColor] = useState('#3b82f6'); // Default blue
  const [bookingSecondaryColor, setBookingSecondaryColor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSelectAddonsModalOpen, setIsSelectAddonsModalOpen] = useState(false);
  const [currentService, setCurrentService] = useState<ExtendedService | null>(null);
  const [selectedServiceAddons, setSelectedServiceAddons] = useState<Record<string, ServiceAddon[]>>({});
  const [isColorPickerModalOpen, setIsColorPickerModalOpen] = useState(false);
  const [isCreateColorPresetModalOpen, setIsCreateColorPresetModalOpen] = useState(false);
  const [colorPresets, setColorPresets] = useState<ColorPreset[]>([]);
  const [selectedColorMethod, setSelectedColorMethod] = useState<'custom' | string>('custom'); // 'custom' or preset id
  
  // ✨ EVENT-DRIVEN PERSISTENCE - Clean, predictable saves on user actions only!
  const { saveModalData, loadModalData, clearModalData } = useEventDrivenModalPersistence('create_booking');

  // Helper function to save current form state
  const saveBookingProgress = (overrides = {}) => {
    const formData = {
      title,
      isLocationBooking,
      selectedCustomer,
      currentVehicle,
      selectedServices,
      // scheduleData deliberately excluded - we don't want to persist schedule across sessions
      bookingColor,
      bookingSecondaryColor,
      selectedServiceAddons,
      selectedResources,
      ...overrides // Allow overriding specific values for immediate saves
    };
    console.log('💾 Saving progress with data:', Object.keys(formData), formData.selectedCustomer ? `Customer: ${formData.selectedCustomer.first_name} ${formData.selectedCustomer.last_name}` : 'No customer');
    saveModalData(formData);
  };

  // Load saved data when modal opens
  useEffect(() => {
    if (isOpen) {
      console.log('📥 Loading CreateBookingModal progress...');
      const savedData = loadModalData();
      
      if (Object.keys(savedData).length > 0) {
        console.log('📥 Restoring saved progress:', Object.keys(savedData));
        console.log('📥 Saved customer data:', savedData.selectedCustomer);
        
        if (savedData.title) {
          setTitle(savedData.title);
          console.log('📥 Restored title:', savedData.title);
        }
        if (savedData.selectedCustomer) {
          setSelectedCustomer(savedData.selectedCustomer);
          console.log('📥 Restored customer:', savedData.selectedCustomer.first_name, savedData.selectedCustomer.last_name);
        }
        if (savedData.currentVehicle) {
          setCurrentVehicle(savedData.currentVehicle);
          console.log('📥 Restored current vehicle:', savedData.currentVehicle.make, savedData.currentVehicle.model);
        }
        if (savedData.selectedServices) {
          setSelectedServices(savedData.selectedServices);
          console.log('📥 Restored services:', savedData.selectedServices.length);
        }
        // scheduleData deliberately not restored - user should start fresh with scheduling
        if (savedData.bookingColor) setBookingColor(savedData.bookingColor);
        if (savedData.bookingSecondaryColor !== undefined) setBookingSecondaryColor(savedData.bookingSecondaryColor);
        if (savedData.selectedServiceAddons) setSelectedServiceAddons(savedData.selectedServiceAddons);
        if (savedData.selectedResources) setSelectedResources(savedData.selectedResources);
        
        console.log('📥 Progress restored successfully');
      } else {
        console.log('📥 No saved progress found');
      }
    }
  }, [isOpen]);

  // Generate time options in 30 minute increments
  const timeOptions = Array.from({ length: 48 }, (_, i) => {
    const hour = Math.floor(i / 2);
    const minute = i % 2 === 0 ? '00' : '30';
    const ampm = hour >= 12 ? 'pm' : 'am';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return {
      value: `${hour.toString().padStart(2, '0')}:${minute}`,
      label: `${displayHour}:${minute} ${ampm}`
    };
  });

  // Calculate total duration in minutes
  const totalDurationMinutes = selectedServices.reduce((total, service) => {
    // Add service duration
    let duration = total + service.priceOption.duration_minutes;
    // Add addon durations for this service
    const addons = selectedServiceAddons[service.service.id] || [];
    duration += addons.reduce((addonSum, addon) => addonSum + addon.duration_minutes, 0);
    return duration;
  }, 0);

  React.useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedBusiness?.id) {
      // Load color presets from database
      loadColorPresets();
    }
  }, [selectedBusiness?.id]);

  const loadColorPresets = async () => {
    if (!selectedBusiness?.id) return;
    
    try {
      const presets = await getColorPresets(selectedBusiness.id);
      setColorPresets(presets);
    } catch (error) {
      console.error('Error loading color presets:', error);
    }
  };

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      clearModalData(); // Clear saved state when modal closes
      // Reset all form fields
      setTitle('Booking');
      setSelectedCustomer(null);
      setCurrentVehicle(null);
      setSelectedServices([]);
      setSelectedResources([]);
      setScheduleData({
        dropOffDate: '',
        dropOffTime: '',
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: '',
        customer_notes: ''
      });
      setBookingColor('#3b82f6');
      setBookingSecondaryColor(null);
      setError(null);

      // This is now a location-only booking modal
      setIsLocationBooking(true);
    }
  };

  const handleVehicleCreated = async (vehicle: CustomerVehicle) => {
    // Update customer data to get the new vehicle
    const updatedCustomer = await getCustomer(selectedCustomer?.id || '');
    setSelectedCustomer(updatedCustomer);
    
    // Set the new vehicle as current and open service selection
    setCurrentVehicle(vehicle);
    setIsCreateVehicleModalOpen(false);
    setIsSelectServiceModalOpen(true);
    
    // Save progress after vehicle creation
    saveBookingProgress();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);

      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      if (!selectedCustomer || !selectedBusiness || selectedServices.length === 0) {
        throw new Error('Please select a customer, business, and at least one service');
      }

      // Removed test trigger to prevent duplicate automation executions
      // The real trigger runs after booking creation with actual data

      // Now continue with normal booking creation flow...
      console.log('📝 Proceeding with actual booking creation...');

      // Create the booking first
      const result = await createBooking({
        business_id: selectedBusiness.id,
        customer_id: selectedCustomer.id,
        title: title || `${selectedCustomer.first_name} ${selectedCustomer.last_name}`,
        start_date: scheduleData.startDate,
        start_time: scheduleData.startTime,
        end_date: scheduleData.endDate,
        end_time: scheduleData.endTime,
        drop_off_date: scheduleData.dropOffDate,
        drop_off_time: scheduleData.dropOffTime,
        status: 'scheduled',
        notes: `Created with ${selectedServices.length} services`,
        customer_notes: scheduleData.customer_notes || "",
        color: bookingColor,
        secondary_color: bookingSecondaryColor || undefined,
        // Group services by vehicle
        vehicles: Object.entries(servicesByVehicle).map(([vehicleId, services]) => ({
          vehicle_id: vehicleId,
          services: services.map(service => {
            const addons = selectedServiceAddons[service.service.id] || [];
            const addonTotal = addons.reduce((sum, addon) => sum + addon.price, 0);
            const servicePrice = service.priceOption.price;
            const taxRate = service.service.tax_exempt ? 0 : (service.service.tax_rate || 5);
            const taxableAmount = servicePrice + addonTotal;
            const taxAmount = service.service.tax_exempt ? 0 : Math.round((taxableAmount * taxRate / 100) * 100) / 100;
            
            return {
              service_id: service.service.id,
              name: service.service.name,
              description: service.service.description || '',
              price: servicePrice,
              tax_exempt: service.service.tax_exempt || false,
              tax_rate: taxRate,
              tax_amount: taxAmount,
              total_amount: taxableAmount + taxAmount,
              duration_minutes: service.priceOption.duration_minutes
            };
          })
        })),
        // Add selected resources if any
        resources: selectedResources.map(resource => ({
          resource_id: resource.id,
          role: 'technician'
        }))
      });

      if (!result || !result.id) throw new Error('No booking data returned');

      // Insert add-ons for each service
      for (const serviceData of selectedServices) {
        const addons = selectedServiceAddons[serviceData.service.id] || [];
        if (addons.length > 0) {
          const { data: serviceResult } = await supabase
            .from('booking_services')
            .select('id')
            .eq('booking_id', result.id)
            .eq('service_id', serviceData.service.id)
            .single();

          if (serviceResult) {
            const { error: addonsError } = await supabase
              .from('booking_service_addons')
              .insert(
                addons.map(addon => ({
                  booking_service_id: serviceResult.id,
                  addon_id: addon.id,
                  price: addon.price
                }))
              );

            if (addonsError) throw addonsError;
          }
        }
      }

      // Execute scheduled triggers after booking is created
      if (selectedBusiness?.id) {
        try {
          // Extract customer information
          const customerInfo = {
            name: `${selectedCustomer.first_name} ${selectedCustomer.last_name}`,
            firstName: selectedCustomer.first_name,
            lastName: selectedCustomer.last_name,
            phone: selectedCustomer.phone || undefined,
            email: selectedCustomer.email || undefined,
            booking: {
              dropOffDate: scheduleData.dropOffDate || undefined,
              dropOffTime: scheduleData.dropOffTime || undefined,
              startDate: scheduleData.startDate || undefined,
              startTime: scheduleData.startTime || undefined,
              endDate: scheduleData.endDate || undefined,
              endTime: scheduleData.endTime || undefined
            }
          };

          // This runs in the background and doesn't block UI
          executeScheduledTriggers(selectedBusiness.id, customerInfo)
            .catch(triggerError => console.error('Error executing scheduled triggers:', triggerError));
          
          console.log('Scheduled triggers initiated for new booking');
        } catch (triggerError) {
          console.error('Failed to execute scheduled triggers:', triggerError);
          // We don't throw here to avoid blocking the booking creation flow
        }


      }

      // Clear the modal persistence data on successful booking creation
      clearModalData();
      console.log('🗑️ Booking created successfully - cleared saved progress');

      // Trigger automations for real booking creation
      try {
        console.log('🤖 REAL BOOKING: Triggering booking automations for created event...');
        
        // Build comprehensive booking data for automation
        const realBookingData = {
          booking_id: result.booking_number || `#${result.id.slice(-4)}`, // Use booking number like "#1134"
          title: title || `${selectedCustomer.first_name} ${selectedCustomer.last_name}`,
          grand_total: selectedServices.reduce((total, service) => {
            const addons = selectedServiceAddons[service.service.id] || [];
            const addonTotal = addons.reduce((sum, addon) => sum + addon.price, 0);
            const servicePrice = service.priceOption.price;
            const taxRate = service.service.tax_exempt ? 0 : (service.service.tax_rate || 5);
            const taxableAmount = servicePrice + addonTotal;
            const taxAmount = service.service.tax_exempt ? 0 : Math.round((taxableAmount * taxRate / 100) * 100) / 100;
            return total + taxableAmount + taxAmount;
          }, 0),
          
          // Customer information
          customer: {
            id: selectedCustomer.id,
            name: `${selectedCustomer.first_name} ${selectedCustomer.last_name}`,
            full_name: `${selectedCustomer.first_name} ${selectedCustomer.last_name}`,
            first_name: selectedCustomer.first_name,
            last_name: selectedCustomer.last_name,
            email: selectedCustomer.email,
            phone: selectedCustomer.phone
          },
          
          // Schedule information
          schedule: {
            start_date: scheduleData.startDate,
            start_time: scheduleData.startTime,
            end_date: scheduleData.endDate,
            end_time: scheduleData.endTime,
            drop_off_date: scheduleData.dropOffDate,
            drop_off_time: scheduleData.dropOffTime
          },
          
          // Services information
          services: selectedServices.map(service => ({
            id: service.service.id,
            name: service.service.name,
            price: service.priceOption.price,
            duration_minutes: service.priceOption.duration_minutes,
            vehicle: {
              id: service.vehicle.id,
              make: service.vehicle.make,
              model: service.vehicle.model,
              year: service.vehicle.year,
              color: service.vehicle.color
            },
            addons: selectedServiceAddons[service.service.id] || []
          })),
          
          // Resources assigned
          resources: selectedResources.map(resource => ({
            id: resource.id,
            name: resource.name,
            phone: resource.phone,
            role: 'technician'
          })),
          
          // Vehicle summary for easy access
          vehicles: (() => {
            const vehicles = selectedServices.map(service => ({
              make: service.vehicle.make || '',
              model: service.vehicle.model || '',
              year: service.vehicle.year || 0,
              color: service.vehicle.color || '',
              display: `${service.vehicle.year || ''} ${service.vehicle.make || ''} ${service.vehicle.model || ''}`.trim()
            }));
            
            // Remove duplicates based on make/model/year combination
            const uniqueVehicles = vehicles.filter((vehicle, index, self) => 
              index === self.findIndex(v => v.make === vehicle.make && v.model === vehicle.model && v.year === vehicle.year)
            );
            
            return uniqueVehicles;
          })(),
          
          // Additional booking details
          notes: scheduleData.customer_notes || null,
          color: bookingColor,
          secondary_color: bookingSecondaryColor || null,
          created_at: new Date().toISOString(),
          status: 'scheduled'
        };

        console.log('📋 Real booking data:', realBookingData);
        
        // Trigger automation (non-blocking)
        triggerBookingAutomations(selectedBusiness.id, 'created', realBookingData)
          .then(() => console.log('🤖 REAL BOOKING: Booking automations completed successfully'))
          .catch(automationError => console.error('❌ REAL BOOKING: Error triggering booking automations:', automationError));
          
        console.log('🤖 REAL BOOKING: Booking automation trigger initiated');
      } catch (automationError) {
        console.error('❌ REAL BOOKING: Failed to trigger booking automations:', automationError);
        // We don't throw here to avoid blocking the booking creation flow
      }

      // Emit custom event to notify other components (like CalendarV2) immediately
      window.dispatchEvent(new CustomEvent('bookingCreated', { 
        detail: { bookingId: result.id, businessId: selectedBusiness.id } 
      }));
      console.log('📅 Dispatched bookingCreated event for immediate calendar update');

      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Error creating booking:', err);
      setError(err instanceof Error ? err.message : 'Failed to create booking. Please check all required fields.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddService = async (service: ExtendedService, priceOption: any) => {
    if (!currentVehicle) return;

    const newService = {
      vehicle: currentVehicle,
      service: {
        ...service,
        tax_rate: service.tax_rate || 5
      },
      priceOption
    };

    // Calculate new services array
    const newServicesArray = [...selectedServices, newService];
    setSelectedServices(newServicesArray);
    
    // Save progress with new services array immediately - bypass timing issues
    saveBookingProgress({ selectedServices: newServicesArray });
    
    // Check if service has addons before opening the modal
    try {
      const availableAddons = await getServiceAddons(service.id);
      
      // Only open addons modal if there are actually addons available
      if (availableAddons && availableAddons.length > 0) {
        console.log(`Service "${service.name}" has ${availableAddons.length} addons available - opening addons modal`);
        setCurrentService(service);
        setIsSelectAddonsModalOpen(true);
      } else {
        console.log(`Service "${service.name}" has no addons available - skipping addons modal`);
        // No addons available, just continue without opening the modal
      }
    } catch (error) {
      console.error('Error checking for service addons:', error);
      // On error, don't open the modal to be safe
    }
  };

  const handleAddAddons = (addons: ServiceAddon[]) => {
    if (!currentService) return;
    
    // Calculate new addons object
    const newAddonsObject = {
      ...selectedServiceAddons,
      [currentService.id]: addons
    };
    setSelectedServiceAddons(newAddonsObject);
    
    // Save progress with new addons immediately - bypass timing issues
    saveBookingProgress({ selectedServiceAddons: newAddonsObject });
  };

  // Group services by vehicle
  const servicesByVehicle = selectedServices.reduce<Record<string, typeof selectedServices>>((acc, service) => {
    const vehicleId = service.vehicle.id;
    if (!acc[vehicleId]) {
      acc[vehicleId] = [];
    }
    acc[vehicleId].push(service);
    return acc;
  }, {});

  const handleSelectVehicle = (vehicle: CustomerVehicle) => {
    setCurrentVehicle(vehicle);
    setIsSelectServiceModalOpen(true);
    setIsSelectVehicleModalOpen(false);
    
    // Save progress after vehicle selection - pass vehicle directly to avoid timing issues
    saveBookingProgress({ currentVehicle: vehicle });
  };

  // Set booking type when modal opens
  useEffect(() => {
    if (isOpen) {
      console.log("Modal opened, setting initial booking type");
      // This is now a location-only booking modal
      setIsLocationBooking(true);
    }
  }, [isOpen]); // Only run when modal opens

  const handleRemoveService = (vehicleId: string, serviceIndex: number) => {
    // Calculate new services array
    const newServicesArray = selectedServices.filter((s, idx) => !(s.vehicle.id === vehicleId && idx === serviceIndex));
    setSelectedServices(newServicesArray);
    
    // Save progress with new services array immediately - bypass timing issues
    saveBookingProgress({ selectedServices: newServicesArray });
  };

  const handleRemoveAddon = (serviceId: string, addonId: string) => {
    // Calculate new addons object
    const newAddonsObject = {
      ...selectedServiceAddons,
      [serviceId]: selectedServiceAddons[serviceId]?.filter(addon => addon.id !== addonId) || []
    };
    setSelectedServiceAddons(newAddonsObject);
    
    // Save progress with new addons immediately - bypass timing issues
    saveBookingProgress({ selectedServiceAddons: newAddonsObject });
  };

  // Enhanced handlers with explicit saves on user actions
  const handleTitleChange = (newTitle: string) => {
    setTitle(newTitle);
    // Save immediately when title changes (user typed something meaningful)
    if (newTitle.trim().length > 0) {
      setTimeout(() => saveBookingProgress(), 500); // Debounce typing
    }
  };

  const handleCustomerSelect = (customer: Customer) => {
    setSelectedCustomer(customer);
    setCurrentVehicle(null);
    setIsSelectCustomerModalOpen(false);
    
    // Save progress after customer selection - pass customer directly to avoid timing issues
    saveBookingProgress({ selectedCustomer: customer });
  };

  const handleScheduleChange = (type: string, date: string, time: string) => {
    // Simple schedule update - no persistence needed for schedule data
    setScheduleData(prev => ({
      ...prev,
      [`${type}Date`]: date,
      [`${type}Time`]: time
    }));
  };

  const handleCustomerNotesChange = (notes: string) => {
    // Simple notes update - no persistence needed for schedule data
    setScheduleData(prev => ({
      ...prev,
      customer_notes: notes
    }));
  };

  const handleColorChange = (color: string) => {
    setBookingColor(color);
    
    // Save progress with new color immediately
    saveBookingProgress({ bookingColor: color });
  };

  const handleResourceAdd = (resource: any) => {
    // Check if resource is already selected
    if (!selectedResources.some(r => r.id === resource.id)) {
      // Calculate new resources array
      const newResourcesArray = [...selectedResources, resource];
      setSelectedResources(newResourcesArray);
      
      // Save progress with new resources immediately - bypass timing issues
      saveBookingProgress({ selectedResources: newResourcesArray });
    }
    setIsSelectResourceModalOpen(false);
  };

  const handleResourceRemove = (resourceId: string) => {
    // Calculate new resources array
    const newResourcesArray = selectedResources.filter(r => r.id !== resourceId);
    setSelectedResources(newResourcesArray);
    
    // Save progress with new resources immediately - bypass timing issues
    saveBookingProgress({ selectedResources: newResourcesArray });
  };

  if (!isOpen && !isAnimating) return null;

  // This is now a location-only booking modal
  return (
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-[10000]`}
      onClick={onClose}
    >
      <div 
        className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onTransitionEnd={handleTransitionEnd}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div className="h-full flex flex-col">
          <div className="px-6 py-4 border-b border-[#2f2f2f] bg-[#1a1a1a] flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h2 className="text-lg font-semibold text-white">Location Booking</h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form 
            onSubmit={handleSubmit} 
            className="flex-1 overflow-y-auto p-6"
          >
            <div className="space-y-8">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  className="w-full h-10 rounded-md bg-gray-50 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm px-4"
                  placeholder="Enter booking title"
                />
              </div>

              {/* Customer Selection */}
              <div className={title ? 'opacity-100' : 'opacity-50 pointer-events-none'}>
                {selectedCustomer ? (
                  <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="h-10 w-10 rounded-full bg-black flex items-center justify-center text-sm font-medium text-white">
                          {selectedCustomer.first_name[0]}{selectedCustomer.last_name[0]}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {selectedCustomer.first_name} {selectedCustomer.last_name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {selectedCustomer.email}
                          </div>
                          {selectedCustomer.phone && (
                            <div className="text-sm text-gray-500">
                              {selectedCustomer.phone}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setIsEditCustomerModalOpen(true);
                          }}
                          className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded-md hover:bg-gray-100"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setIsSelectCustomerModalOpen(true);
                          }}
                          className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                        >
                          Change
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsSelectCustomerModalOpen(true);
                    }}
                    className="w-full px-4 py-4 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:text-gray-900 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Select Customer
                  </button>
                )}

                {/* Vehicle Selection */}
                {selectedCustomer && 
                  <div className="mt-6">
                    {/* Display vehicles with their services */}
                    {Object.entries(servicesByVehicle).map(([vehicleId, services]) => {
                      const vehicle = services[0].vehicle;
                      return (
                        <div key={vehicleId} className="mb-6">
                          <div className="bg-gray-50 rounded-t-lg border border-gray-200 p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center justify-between flex-1">
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {vehicle.year} {vehicle.make} {vehicle.model}
                                  </div>
                                  {vehicle.color && (
                                    <div className="text-sm text-gray-500">{vehicle.color}</div>
                                  )}
                                </div>
                                <div className="flex items-center space-x-3">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedVehicleToEdit(vehicle);
                                      setIsEditVehicleModalOpen(true);
                                    }}
                                    className="text-xs text-blue-600 hover:text-blue-800"
                                  >
                                    Edit
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Services for this vehicle */}
                          {services.map((service, index) => (
                            <div key={index} className="bg-white p-4 border-x border-b border-gray-200">
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center">
                                    <div className="text-sm font-medium text-gray-900">
                                      {service.service.name}
                                    </div>
                                    {vehicle.is_primary && (
                                      <span className="ml-2 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                                        Primary
                                      </span>
                                    )}
                                  </div>
                                  <div className="mt-1 text-sm text-gray-500">
                                    {service.priceOption.duration_minutes >= 1440 
                                      ? `${Math.round(service.priceOption.duration_minutes / 1440)} days`
                                      : `${Math.round(service.priceOption.duration_minutes / 60)} hours`
                                    }
                                  </div>
                                </div>
                                <div className="flex items-center space-x-4">
                                  <div className="text-sm font-medium text-gray-900">
                                    ${service.priceOption.price.toLocaleString()}
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setCurrentService(service.service);
                                      setIsSelectAddonsModalOpen(true);
                                    }}
                                    className="text-blue-600 hover:text-blue-800"
                                  >
                                    Add-ons
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setSelectedServiceToEdit(service);
                                      setIsEditServiceModalOpen(true);
                                    }}
                                    className="text-gray-600 hover:text-gray-900"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveService(vehicle.id, index)}
                                    className="text-red-600 hover:text-red-800"
                                  >
                                    Remove
                                  </button>
                                </div>
                              </div>
                              {/* Display selected add-ons */}
                              {selectedServiceAddons[service.service.id]?.length > 0 && (
                                <div className="mt-2">
                                  <div className="text-xs font-medium text-gray-500 mb-1">Add-ons:</div>
                                  <div className="space-y-1">
                                    {selectedServiceAddons[service.service.id].map(addon => (
                                      <div key={addon.id} className="flex items-center justify-between text-sm">
                                        <div>
                                          <span className="text-gray-600">{addon.name}</span>
                                          <div className="text-xs text-gray-500">
                                            {addon.duration_minutes >= 1440 
                                              ? `${Math.round(addon.duration_minutes / 1440)} days`
                                              : `${Math.round(addon.duration_minutes / 60)} hours`
                                            }
                                          </div>
                                        </div>
                                        <div className="flex items-center space-x-3">
                                          <span className="text-gray-900">${addon.price.toLocaleString()}</span>
                                          <button
                                            type="button"
                                            onClick={() => handleRemoveAddon(service.service.id, addon.id)}
                                            className="text-xs text-red-600 hover:text-red-800"
                                          >
                                            Remove
                                          </button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}

                          {/* Add service button for this vehicle */}
                          <div className="mt-4">
                            <button
                              type="button"
                              onClick={() => {
                                setCurrentVehicle(vehicle);
                                setIsSelectServiceModalOpen(true);
                              }}
                              className="w-full flex items-center justify-center px-4 py-4 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:text-gray-900 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                            >
                              Add Service
                            </button>
                          </div>
                        </div>
                      );
                    })}

                    {/* Add new vehicle button */}
                    {selectedServices.length === 0 ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsSelectVehicleModalOpen(true);
                        }}
                        className="w-full px-4 py-4 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:text-gray-900 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                      >
                        Select Vehicle
                      </button>
                    ) : (
                      <div className="mt-6">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setIsSelectVehicleModalOpen(true);
                          }}
                          className="w-full px-4 py-4 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:text-gray-900 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                        >
                          Add Another Vehicle
                        </button>
                      </div>
                    )}

                    {/* Add service button when vehicle is selected but no services yet */}
                    {currentVehicle && !servicesByVehicle[currentVehicle.id] && (
                      <div className="mt-4">
                        <button
                          type="button"
                          onClick={() => setIsSelectServiceModalOpen(true)}
                          className="w-full flex items-center justify-center px-4 py-4 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:text-gray-900 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                        >
                          Add Service
                        </button>
                      </div>
                    )}
                  </div>
                }
                
                {/* Overall Summary */}
                {selectedServices.length > 0 && (
                  <div className="mt-8 bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-sm font-medium text-gray-900 mb-4">Pricing Summary</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>Total Duration</span>
                        <span className="flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          {(() => {
                            const totalMinutes = selectedServices.reduce((sum, s) => {
                              // Add service duration
                              let total = sum + s.priceOption.duration_minutes;
                              // Add addon durations for this service
                              const addons = selectedServiceAddons[s.service.id] || [];
                              total += addons.reduce((addonSum, addon) => addonSum + addon.duration_minutes, 0);
                              return total;
                            }, 0);
                            return totalMinutes >= 1440 
                              ? `${Math.round(totalMinutes / 1440)} days`
                              : `${Math.round(totalMinutes / 60)} hours`;
                          })()}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>Subtotal</span>
                        <span>${(selectedServices.reduce((sum, s) => {
                          // Add service price
                          let total = sum + s.priceOption.price;
                          // Add addon prices for this service
                          const addons = selectedServiceAddons[s.service.id] || [];
                          total += addons.reduce((addonSum, addon) => addonSum + addon.price, 0);
                          return total;
                        }, 0)).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>GST (5%)</span>
                        <span>+ ${(selectedServices.reduce((sum, s) => {
                          // Calculate GST on service price
                          let total = sum + s.priceOption.price;
                          // Add addon prices for this service
                          const addons = selectedServiceAddons[s.service.id] || [];
                          total += addons.reduce((addonSum, addon) => addonSum + addon.price, 0);
                          // Only apply tax if service is not tax exempt
                          return s.service.tax_exempt ? sum : sum + (total * 0.05);
                        }, 0)).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-sm font-medium text-gray-900 pt-2 border-t border-gray-200">
                        <span>Total</span>
                        <span>${(selectedServices.reduce((sum, s) => {
                          // Calculate service total with tax
                          let total = s.priceOption.price;
                          // Add addon prices for this service
                          const addons = selectedServiceAddons[s.service.id] || [];
                          total += addons.reduce((addonSum, addon) => addonSum + addon.price, 0);
                          // Add tax if not exempt
                          if (!s.service.tax_exempt) {
                            total += total * 0.05;
                          }
                          return sum + total;
                        }, 0)).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Customer Notes */}
                {selectedServices.length > 0 && (
                  <div className="mt-8 bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-sm font-medium text-gray-900 mb-4">Customer Notes</h3>
                    <textarea
                      value={scheduleData.customer_notes || ''}
                      onChange={(e) => handleCustomerNotesChange(e.target.value)}
                      placeholder="Enter any notes about the customer or booking"
                      className="w-full h-32 rounded-md bg-gray-50 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm px-4 py-2"
                    />
                  </div>
                )}

                {/* Scheduling Section */}
                {selectedServices.length > 0 && (
                  <div className="mt-8 bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-sm font-medium text-gray-900 mb-4">Schedule</h3>
                    <CreateScheduleEditor
                      dropOffDate={scheduleData.dropOffDate}
                      dropOffTime={scheduleData.dropOffTime}
                      startDate={scheduleData.startDate}
                      startTime={scheduleData.startTime}
                      endDate={scheduleData.endDate}
                      endTime={scheduleData.endTime}
                      serviceDuration={totalDurationMinutes}
                      onDateTimeChange={(type, date, time) => {
                        handleScheduleChange(type, date, time);
                      }}
                      disabled={loading}
                    />
                  </div>
                )}
                
                {/* Resource Selection */}
                {selectedServices.length > 0 && (
                  <div className="mt-8 bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-sm font-medium text-gray-900 mb-4">Resources</h3>
                    
                    {selectedResources.length > 0 ? (
                      <div className="space-y-3">
                        {selectedResources.map((resource) => (
                          <div key={resource.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-4">
                            <div className="flex items-center space-x-3">
                              <div className="h-10 w-10 rounded-full bg-black flex items-center justify-center text-sm font-medium text-white">
                                {resource.first_name[0]}{resource.last_name[0]}
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {resource.first_name} {resource.last_name}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {resource.email}
                                </div>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleResourceRemove(resource.id)}
                              className="text-xs text-red-600 hover:text-red-800"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => setIsSelectResourceModalOpen(true)}
                          className="w-full mt-4 flex items-center justify-center px-4 py-4 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:text-gray-900 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                        >
                          Add Another Resource
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setIsSelectResourceModalOpen(true)}
                        className="w-full px-4 py-4 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:text-gray-900 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                      >
                        Add Resources
                      </button>
                    )}
                  </div>
                )}

                {/* Color Picker */}
                {selectedServices.length > 0 && (
                  <div className="mt-8 bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-sm font-medium text-gray-900 mb-4">Booking Colour</h3>
                    <div className="flex items-center flex-wrap gap-3">
                      {/* Default Choose Colour */}
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedColorMethod('custom');
                          setIsColorPickerModalOpen(true);
                        }}
                        className={`flex flex-col items-center space-y-2 px-4 py-3 border-2 rounded-md hover:bg-gray-50 focus:outline-none transition-colors ${
                          selectedColorMethod === 'custom' 
                            ? 'border-red-500 bg-red-50' 
                            : 'border-gray-300'
                        }`}
                      >
                        <span className="text-sm text-gray-700">Current Colour</span>
                        <div className="w-10 h-10 rounded-full border border-gray-300 overflow-hidden">
                          {bookingSecondaryColor ? (
                            <div className="w-full h-full flex">
                              <div 
                                className="w-1/2 h-full"
                                style={{ backgroundColor: bookingColor }}
                              />
                              <div 
                                className="w-1/2 h-full"
                                style={{ backgroundColor: bookingSecondaryColor }}
                              />
                            </div>
                          ) : (
                            <div 
                              className="w-full h-full"
                              style={{ backgroundColor: bookingColor }}
                            />
                          )}
                        </div>
                      </button>

                      {/* Color Presets */}
                      {colorPresets.map((preset) => (
                        <div key={preset.id} className="relative">
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedColorMethod(preset.id);
                              setBookingColor(preset.primary_color);
                              setBookingSecondaryColor(preset.secondary_color || null);
                              saveBookingProgress({ 
                                bookingColor: preset.primary_color, 
                                bookingSecondaryColor: preset.secondary_color 
                              });
                            }}
                            className={`flex flex-col items-center space-y-2 px-4 py-3 border-2 rounded-md hover:bg-gray-50 focus:outline-none transition-colors ${
                              selectedColorMethod === preset.id 
                                ? 'border-red-500 bg-red-50' 
                                : 'border-gray-300'
                            }`}
                          >
                            <span className="text-sm text-gray-700">{preset.title}</span>
                            <div className="w-10 h-10 rounded-full border border-gray-300 overflow-hidden">
                              {preset.secondary_color ? (
                                <div className="w-full h-full flex">
                                  <div 
                                    className="w-1/2 h-full"
                                    style={{ backgroundColor: preset.primary_color }}
                                  />
                                  <div 
                                    className="w-1/2 h-full"
                                    style={{ backgroundColor: preset.secondary_color }}
                                  />
                                </div>
                              ) : (
                                <div 
                                  className="w-full h-full"
                                  style={{ backgroundColor: preset.primary_color }}
                                />
                              )}
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={async (e) => {
                              e.stopPropagation();
                              
                              // Show confirmation dialog
                              const confirmed = window.confirm(
                                `Are you sure you want to delete the "${preset.title}" color preset? This action cannot be undone.`
                              );
                              
                              if (!confirmed) return;
                              
                              try {
                                await deleteColorPreset(preset.id);
                                setColorPresets(prev => prev.filter(p => p.id !== preset.id));
                                if (selectedColorMethod === preset.id) {
                                  setSelectedColorMethod('custom');
                                }
                              } catch (error) {
                                console.error('Error deleting color preset:', error);
                              }
                            }}
                            className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-xs font-bold shadow-sm"
                          >
                            ×
                          </button>
                        </div>
                      ))}

                      {/* Add Preset Button */}
                      <button
                        type="button"
                        onClick={() => setIsCreateColorPresetModalOpen(true)}
                        className="flex items-center justify-center w-8 h-8 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

                <div className="mt-8">
                  <button
                    type="submit"
                    disabled={loading || !title || !selectedCustomer || selectedServices.length === 0}
                    className="w-full px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Creating...' : 'Create Booking'}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      <SelectCustomerModal
        isOpen={isSelectCustomerModalOpen}
        onClose={() => setIsSelectCustomerModalOpen(false)}
        onSelect={handleCustomerSelect}
      />

      <SelectVehicleModal
        isOpen={isSelectVehicleModalOpen}
        onClose={() => setIsSelectVehicleModalOpen(false)}
        onSelect={handleSelectVehicle}
        vehicles={selectedCustomer?.vehicles || []}
        selectedVehicles={selectedServices.map(s => s.vehicle)}
        onAddVehicle={() => {
          setIsSelectVehicleModalOpen(false);
          setIsCreateVehicleModalOpen(true);
        }}
      />

      <CreateVehicleModal
        isOpen={isCreateVehicleModalOpen}
        onClose={() => setIsCreateVehicleModalOpen(false)}
        onSuccess={handleVehicleCreated}
        customerId={selectedCustomer?.id || ''}
      />

      <EditCustomerModal
        isOpen={isEditCustomerModalOpen}
        onClose={() => setIsEditCustomerModalOpen(false)}
        onSuccess={(updatedCustomer) => {
          setIsEditCustomerModalOpen(false);
          setSelectedCustomer(updatedCustomer);
        }}
        customer={selectedCustomer}
      />

      <SelectServiceModal
        isOpen={isSelectServiceModalOpen}
        onClose={() => setIsSelectServiceModalOpen(false)}
        onAdd={async (service, priceOption) => {
          handleAddService(service, priceOption);
          return Promise.resolve();
        }}
      />

      <SelectResourceModal
        isOpen={isSelectResourceModalOpen}
        onClose={() => setIsSelectResourceModalOpen(false)}
        onSelect={handleResourceAdd}
        selectedResources={selectedResources}
      />

      <EditVehicleModal
        isOpen={isEditVehicleModalOpen}
        onClose={() => {
          setIsEditVehicleModalOpen(false);
          setSelectedVehicleToEdit(null);
        }}
        onUpdate={(updatedVehicle: CustomerVehicle) => {
          // Update the vehicle in the selectedServices array
          setSelectedServices(prev => prev.map(s => 
            s.vehicle.id === updatedVehicle.id
              ? { ...s, vehicle: updatedVehicle }
              : s
          ));
          setIsEditVehicleModalOpen(false);
          setSelectedVehicleToEdit(null);
        }}
        vehicle={selectedVehicleToEdit}
      />

      <EditServiceModal
        isOpen={isEditServiceModalOpen}
        onClose={() => {
          setIsEditServiceModalOpen(false);
          setSelectedServiceToEdit(null);
        }}
        onSave={async (updatedService) => {
          // Update the service in the selectedServices array
          setSelectedServices(prev => prev.map(s => 
            s === selectedServiceToEdit
              ? {
                  ...s,
                  service: {
                    ...s.service,
                    name: updatedService.name,
                    description: updatedService.description
                  },
                  priceOption: {
                    ...s.priceOption,
                    price: updatedService.price,
                    duration_minutes: updatedService.duration_minutes
                  }
                }
              : s
          ));
          setIsEditServiceModalOpen(false);
          setSelectedServiceToEdit(null);
        }}
        service={selectedServiceToEdit ? {
          name: selectedServiceToEdit.service.name,
          description: selectedServiceToEdit.service.description,
          price: selectedServiceToEdit.priceOption.price,
          duration_minutes: selectedServiceToEdit.priceOption.duration_minutes,
          is_active: true,
          tax_rate: selectedServiceToEdit.service.tax_rate || 5,
          tax_exempt: selectedServiceToEdit.service.tax_exempt || false,
          addons: selectedServiceAddons[selectedServiceToEdit.service.id] || []
        } : null}
      />

      <SelectServiceAddonsModal
        isOpen={isSelectAddonsModalOpen}
        onClose={() => {
          setIsSelectAddonsModalOpen(false);
          setCurrentService(null);
        }}
        serviceId={currentService?.id || ''}
        onSelect={handleAddAddons}
        selectedAddons={currentService ? selectedServiceAddons[currentService.id] || [] : []}
      />

      <ColorPickerModal
        isOpen={isColorPickerModalOpen}
        onClose={() => setIsColorPickerModalOpen(false)}
        onColorSelect={(color, secondaryColor) => {
          setSelectedColorMethod('custom');
          setBookingColor(color);
          setBookingSecondaryColor(secondaryColor || null);
          saveBookingProgress({ bookingColor: color, bookingSecondaryColor: secondaryColor });
          setIsColorPickerModalOpen(false);
        }}
        currentColor={bookingColor}
        currentSecondaryColor={bookingSecondaryColor || undefined}
      />

      <CreateColorPresetModal
        isOpen={isCreateColorPresetModalOpen}
        onClose={() => setIsCreateColorPresetModalOpen(false)}
        onSave={async (title, primaryColor, secondaryColor) => {
          if (!selectedBusiness?.id) return;
          
          try {
            const newPreset = await createColorPreset({
              business_id: selectedBusiness.id,
              title,
              primary_color: primaryColor,
              secondary_color: secondaryColor
            });
            setColorPresets(prev => [...prev, newPreset]);
            setIsCreateColorPresetModalOpen(false);
          } catch (error) {
            console.error('Error creating color preset:', error);
          }
        }}
      />
    </div>
  );
}